'use client';

/**
 * RealtimeContext — single source of truth for all live badge counts.
 *
 * Maintains ONE set of Supabase real-time channels for the authenticated user
 * and exposes reactive counts to every consumer (Sidebar, NotificationHub,
 * MessagesContent, etc.) so all open screens update instantly without a reload.
 *
 * Tracked events:
 *  • unreadDmCount      — unread direct_messages where receiver_id = me
 *  • unreadNotifCount   — unread notifications where recipient_id = me
 *  • newFollowCount     — new follows where following_id = me (since last viewed)
 *  • newCommentCount    — new comment notifications (subset of unreadNotifCount)
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { createClient } from '@/lib/local-api/client';
import { useAuth } from '@/contexts/AuthContext';

interface RealtimeState {
  unreadDmCount: number;
  unreadNotifCount: number;
  newFollowCount: number;
  newCommentCount: number;
  /** Call after user reads DMs to reset the badge */
  markDmsRead: () => void;
  /** Call after user views notifications to reset the badge */
  markNotifsRead: () => void;
  /** Call after user views follows to reset the badge */
  markFollowsRead: () => void;
  /** Force-refresh all counts (e.g. after marking items read in the DB) */
  refreshAll: () => void;
}

const RealtimeContext = createContext<RealtimeState>({
  unreadDmCount: 0,
  unreadNotifCount: 0,
  newFollowCount: 0,
  newCommentCount: 0,
  markDmsRead: () => {},
  markNotifsRead: () => {},
  markFollowsRead: () => {},
  refreshAll: () => {},
});

export const useRealtime = () => useContext(RealtimeContext);

export const RealtimeProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const supabase = createClient();

  const [unreadDmCount, setUnreadDmCount] = useState(0);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [newFollowCount, setNewFollowCount] = useState(0);
  const [newCommentCount, setNewCommentCount] = useState(0);

  // Track the last-seen follow timestamp so we only count truly new ones
  const lastFollowSeenRef = useRef<string>(new Date().toISOString());

  // ── Fetch helpers ──────────────────────────────────────────────────────────

  const fetchDmCount = useCallback(async (uid: string) => {
    const { count } = await supabase
      .from('direct_messages')
      .select('id', { count: 'exact', head: true })
      .eq('receiver_id', uid)
      .is('read_at', null);
    setUnreadDmCount(count ?? 0);
  }, [supabase]);

  const fetchNotifCount = useCallback(async (uid: string) => {
    const { count } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('recipient_id', uid)
      .eq('is_read', false);
    setUnreadNotifCount(count ?? 0);
  }, [supabase]);

  const fetchCommentCount = useCallback(async (uid: string) => {
    const { count } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('recipient_id', uid)
      .eq('is_read', false)
      .eq('notification_type', 'comment');
    setNewCommentCount(count ?? 0);
  }, [supabase]);

  const fetchFollowCount = useCallback(async (uid: string) => {
    const { count } = await supabase
      .from('follows')
      .select('id', { count: 'exact', head: true })
      .eq('following_id', uid)
      .gt('created_at', lastFollowSeenRef.current);
    setNewFollowCount(count ?? 0);
  }, [supabase]);

  const refreshAll = useCallback(() => {
    if (!user) return;
    fetchDmCount(user.id);
    fetchNotifCount(user.id);
    fetchCommentCount(user.id);
    fetchFollowCount(user.id);
  }, [user, fetchDmCount, fetchNotifCount, fetchCommentCount, fetchFollowCount]);

  // ── Reset helpers (called by consumers after viewing) ─────────────────────

  const markDmsRead = useCallback(() => {
    if (user) void fetchDmCount(user.id);
  }, [user, fetchDmCount]);
  const markNotifsRead = useCallback(() => {
    setUnreadNotifCount(0);
    setNewCommentCount(0);
  }, []);
  const markFollowsRead = useCallback(() => {
    lastFollowSeenRef.current = new Date().toISOString();
    setNewFollowCount(0);
  }, []);

  // ── Real-time subscriptions ────────────────────────────────────────────────

  useEffect(() => {
    if (!user) {
      setUnreadDmCount(0);
      setUnreadNotifCount(0);
      setNewFollowCount(0);
      setNewCommentCount(0);
      return;
    }

    const uid = user.id;

    // Initial fetch
    fetchDmCount(uid);
    fetchNotifCount(uid);
    fetchCommentCount(uid);
    fetchFollowCount(uid);

    // ── Channel: direct_messages ──
    const dmChannel = supabase
      .channel(`rt_dm_${uid}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'direct_messages',
          filter: `receiver_id=eq.${uid}`,
        },
        () => fetchDmCount(uid)
      )
      .subscribe();

    // ── Channel: notifications ──
    const notifChannel = supabase
      .channel(`rt_notif_${uid}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${uid}`,
        },
        () => {
          fetchNotifCount(uid);
          fetchCommentCount(uid);
        }
      )
      .subscribe();

    // ── Channel: follows ──
    const followChannel = supabase
      .channel(`rt_follow_${uid}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'follows',
          filter: `following_id=eq.${uid}`,
        },
        () => fetchFollowCount(uid)
      )
      .subscribe();

    return () => {
      supabase.removeChannel(dmChannel);
      supabase.removeChannel(notifChannel);
      supabase.removeChannel(followChannel);
    };
  }, [user, supabase, fetchDmCount, fetchNotifCount, fetchCommentCount, fetchFollowCount]);

  return (
    <RealtimeContext.Provider
      value={{
        unreadDmCount,
        unreadNotifCount,
        newFollowCount,
        newCommentCount,
        markDmsRead,
        markNotifsRead,
        markFollowsRead,
        refreshAll,
      }}
    >
      {children}
    </RealtimeContext.Provider>
  );
};
